import { REFUSAL_CONCERNS, readRemedy, type AccessRemedy, type RefusalConcern } from "../access/decisions";
import { enumMembers, type ApiSchema } from "../apiSchema";
import { readObject, readOneOf } from "../http/jsonDecoding";

/**
 * What the caller may do on a review now, as module B decides it for them (SOT-02): each action by the rule its
 * command refuses from (`server/src/dmw/content/reviews/actions.py`), decoded from the review body's `actions`.
 */

export type ReviewActionName = ApiSchema<"ReviewActionName">;
/** Why module B refuses a review action; module A's code for a permission the caller lacks; module C's for its review step. */
export type ReviewRefusal = ApiSchema<"ReviewRefusal"> | ApiSchema<"PermissionRefusal"> | ApiSchema<"StepRefusal">;

export interface ReviewAction {
  readonly action: ReviewActionName;
  /** `null` when the caller may take it now. */
  readonly refusal: ReviewRefusal | null;
  readonly concern: RefusalConcern | null;
  /** What would open it, for a permission the caller lacks (module A's remedy). */
  readonly remedy: AccessRemedy | null;
}

const REVIEW_ACTIONS = enumMembers<ReviewActionName>({
  approve: true,
  request_changes: true,
  scrap: true,
  merge: true,
  resubmit: true,
  edit: true,
  request_review: true,
  propose_candidate: true,
  rerun_conflict_check: true,
});
const REVIEW_REFUSALS = enumMembers<ReviewRefusal>({
  nothing_to_review: true,
  change_set_state: true,
  curates_none: true,
  approvals_missing: true,
  merge_blocked: true,
  missing_permission: true,
  step_not_answerable: true,
});

export function readReviewAction(value: unknown): ReviewAction {
  const action = readObject(value, "review action");
  return {
    action: readOneOf(action, "action", REVIEW_ACTIONS),
    refusal: action["refusal"] == null ? null : readOneOf(action, "refusal", REVIEW_REFUSALS),
    concern: action["concern"] == null ? null : readOneOf(action, "concern", REFUSAL_CONCERNS),
    remedy: readRemedy(action["remedy"]),
  };
}
